import React from "react"
import { motion, type HTMLMotionProps } from "framer-motion"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"

interface RevealProps extends HTMLMotionProps<"div"> {
  children: React.ReactNode
  className?: string
  delay?: number
  disabled?: boolean
  /** Enable lazy loading for below-fold sections (BUG-025) */
  lazy?: boolean
}

export function Reveal({ children, className, delay = 0, disabled = false, lazy = false, ...props }: RevealProps) {
  // Canonical motion duration (240ms) instead of 500ms (BUG-004)
  const motionDuration = 0.24;
  
  // Lazy loading: only render when in viewport (BUG-025)
  const [shouldRender, setShouldRender] = React.useState(!lazy);
  
  React.useEffect(() => {
    if (!lazy || shouldRender) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start loading 200px before viewport
    );
    
    const placeholder = document.getElementById(`reveal-placeholder-${delay}`);
    if (placeholder) {
      observer.observe(placeholder);
    }
    
    return () => observer.disconnect();
  }, [lazy, shouldRender, delay]);
  
  if (lazy && !shouldRender) {
    return <div id={`reveal-placeholder-${delay}`} className="min-h-[400px]" aria-hidden="true" />;
  }
  
  if (disabled) {
    return <div className={cn(className)} {...props}>{children}</div>;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: motionDuration, delay, ease: "easeOut" }}
      className={cn(className)}
      {...props}
    >
      {children}
    </motion.div>
  )
}

